/**
 * useWarmupStatus — per-task "Waking…" indicator signal.
 *
 * Derives the warming flag from displayStatus AND the turn's PLACEMENT — where
 * the turn actually runs. It used to gate on the task's runtime (`native` meant
 * "runs on the LOCAL runner", PR #1812), but since 2026-08-12 every task
 * launches native: the arm is a constant, so the runtime gate silently switched
 * the boot line OFF for every web task. Runtime no longer encodes placement.
 *
 * Placement does: the web surface is the CLOUD lane (the turn runs on the user's
 * sandbox, so the sandbox coming up IS the wait the person is watching), while
 * inside the desktop shell the turn runs on the user's own machine and a
 * hibernating cloud sandbox re-warming in the background must not caption it
 * (founder 07-13: the label showed on every native-app message).
 *
 * Why a hook and not inlined: the `lastMessage.role === 'user'` gate is
 * applied at the MessageList level, but knowing WHETHER to show the
 * indicator is a pure function of (displayStatus, placement). Keeping it in a
 * hook lets other surfaces (future admin, browser-side debug) reuse it.
 */

import { useEffect, useState } from "react";
import { useConnectionStore } from "@/stores/connectionStore";
import { isDesktopShell } from "@/lib/desktop-shell";

/**
 * The banner earns its seconds. Wakes are 0.5-4s on the measured path (typing
 * pre-warm lands most under 1s), and painting "Waking your workspace…" over a
 * sub-second wake reads as infrastructure drama for a wait the user barely
 * had (founder 08-28, off Ankur's report: "if wakes are genuinely under 1s
 * then there is no point for this, we can show this after 4s"). Until the
 * threshold, the ordinary start presentation owns the gap; only a wake still
 * going at 4s is a wait worth naming.
 */
export const WARMUP_BANNER_DELAY_MS = 4_000;

export type Placement = "cloud" | "local";

/** Pure: sandbox warmup gates only turns that RUN on the sandbox (cloud placement). */
export function warmingFor(status: string, placement: Placement): boolean {
  if (placement === "local") return false;
  // 'starting' is the one state that means "sandbox is coming up and user
  // is waiting on it". Upgrades show a different chrome ('Updating…'),
  // gated separately by isUpgrading in MessageList.
  return status === "starting";
}

/** The web surface is the cloud lane; inside the desktop shell the turn runs on the user's own machine. */
export function usePlacement(): Placement {
  return typeof window !== "undefined" && isDesktopShell() ? "local" : "cloud";
}

export function useWarmupStatus(_taskId: string | null | undefined): {
  warming: boolean;
  placement: Placement;
} {
  const status = useConnectionStore((s) => s.connectionState.status);
  const placement = usePlacement();
  const waking = warmingFor(status, placement);
  // Sustained-only: each wake runs its own 4s clock, reset the moment the
  // status leaves 'starting', so a threshold from one wake never leaks into
  // the next.
  const [sustained, setSustained] = useState(false);
  useEffect(() => {
    if (!waking) {
      setSustained(false);
      return;
    }
    const timer = setTimeout(() => setSustained(true), WARMUP_BANNER_DELAY_MS);
    return () => clearTimeout(timer);
  }, [waking]);
  return { warming: waking && sustained, placement };
}
